import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, Popup as Pop, Lookup, RequiredRule, Toolbar, Item } from 'devextreme-react/data-grid';
import { Button, Tooltip } from 'devextreme-react';
import { useApiConfiguration } from '../../../api/api.configuration';
import DataSource from 'devextreme/data/data_source';
import CustomStore from 'devextreme/data/custom_store';
import { useAppSelector } from '../../../contexts/hooks';
import { ModalRangosExtendidos } from '../Components/ModalRangosExtendidos';
import { useScreenSize } from '../../../utils/media-query';

export const ReferencesValues = ({ IdAnalito, analitoNombre }: { IdAnalito: number; analitoNombre?: string }) => {
  const { getReferencesValues, setAnalitosValRef, newAnalitosValRef, deleteAnalitosValRef } = useApiConfiguration();
  const dataRef = useRef<DataGrid>(null);
  const Sedes = useAppSelector((x) => x.configuration.Data?.ComboBox[0]);
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  // Estado para la modal de rangos extendidos
  const [isModalRangosVisible, setIsModalRangosVisible] = useState(false);

  // Usar el nombre pasado como prop o un fallback
  const nombreAnalito = analitoNombre || `Analito ${IdAnalito}`;

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Avr_Id',
      load() {
        return getReferencesValues(IdAnalito)
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      insert: async (values) => {
        const newValues = {
          ...values,
          Avr_An_Id: IdAnalito,
        };
        return newAnalitosValRef({ ValRef: newValues })
          .then((res) => {
            if (res === 105) {
              dataRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error insertando valor de referencia');
          });
      },
      update: (key, values) => {
        return setAnalitosValRef({ ValRef: values, IdValRef: key })
          .then((res) => {
            if (res === 107) {
              dataRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error actualizando el valor de referencia');
          });
      },
      remove: (key) => {
        return deleteAnalitosValRef({ IdValRef: key }).then((res) => {
          if (res === 107) {
            dataRef.current?.instance.refresh();
          }
        });
      },
    });
  }, [IdAnalito]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleRangosExtendidosClick = useCallback(() => {
    setIsModalRangosVisible(true);
  }, []);

  const handleRangosExtendidosClose = useCallback(() => {
    setIsModalRangosVisible(false);
    // Opcional: refrescar datos si es necesario
    dataRef.current?.instance.refresh();
  }, []);

  return (
    <div>
      <DataGrid className='grid' dataSource={customDataSrc} width={'100%'} hoverStateEnabled={true} height='100%' rowAlternationEnabled={true}>
        {/* Toolbar con botón de rangos extendidos */}
        <Toolbar>
          <Item location='before' template={() => <div className='grid-header'>Valores de Referencia</div>} />
          <Item name='addRowButton' />
          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RangosExtendidos' icon='range' text='Rangos extendidos' stylingMode='text' visible={true} disabled={false} onClick={handleRangosExtendidosClick} />
            <Tooltip target={'#RangosExtendidos'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Rangos extendidos por edad y género
            </Tooltip>
          </Item>
        </Toolbar>
        <Editing mode={isSmall || isXSmall || isMedium ? 'popup' : 'form'} allowUpdating={true} allowAdding={true} allowDeleting={true} />
        <Column allowGrouping={false} caption='SEDE' visible={true} allowEditing={true} dataField='Avr_Sd_Id' alignment='center'>
          <Lookup dataSource={Sedes?.Data} valueExpr='Sd_Id' displayExpr='Sd_Nombre' />
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} minWidth={90} caption='MIN. HOMBRE' visible={true} allowEditing={true} dataField='Avr_MinimoHombre' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MAX. HOMBRE' visible={true} allowEditing={true} dataField='Avr_MaximoHombre' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MIN. MUJER' visible={true} allowEditing={true} dataField='Avr_MinimoMujer' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MAX. MUJER' visible={true} allowEditing={true} dataField='Avr_MaximoMujer' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MIN. EMBARAZO' visible={true} allowEditing={true} dataField='Avr_MinimoEmbarazo' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MAX. EMBARAZO' visible={true} allowEditing={true} dataField='Avr_MaximoEmbarazo' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MIN. ALARMA' visible={true} allowEditing={true} dataField='Avr_MinimoAlarma' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MAX. ALARMA' visible={true} allowEditing={true} dataField='Avr_MaximoAlarma' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MIN. ABSURDO' visible={true} allowEditing={true} dataField='Avr_MinimoAbsurdo' alignment='center' />
        <Column allowGrouping={false} minWidth={90} caption='MAX. ABSURDO' visible={true} allowEditing={true} dataField='Avr_MaximoAbsurdo' alignment='center' />
        <Column type='buttons' caption={'ACCIÓN'} width={100}>
          <Butt name='edit' />
          <Butt name='delete' />
        </Column>
      </DataGrid>

      {/* Modal de rangos extendidos */}
      <ModalRangosExtendidos visible={isModalRangosVisible} setVisible={setIsModalRangosVisible} analitoId={IdAnalito} analitoNombre={nombreAnalito} onSave={handleRangosExtendidosClose} />
    </div>
  );
};
